import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { FaDatabase } from 'react-icons/fa';
import './StyledDataTable.css';


/* ── Mobile card view helper ── */
const MobileCardList = ({
  columns,
  data,
  selectableRows,
  onSelectedRowsChange,
  clearSelectedRows,
  onRowClicked
}) => {
  const [selectedMobileRows, setSelectedMobileRows] = useState([]);

  useEffect(() => {
    setSelectedMobileRows([]);
  }, [clearSelectedRows, selectableRows]);

  const handleToggleRow = (row, e) => {
    if (e) e.stopPropagation();
    let newSelected;
    const exists = selectedMobileRows.some(r => String(r.id ?? r) === String(row.id ?? row));
    if (exists) {
      newSelected = selectedMobileRows.filter(r => String(r.id ?? r) !== String(row.id ?? row));
    } else {
      newSelected = [...selectedMobileRows, row];
    }
    setSelectedMobileRows(newSelected);
    if (onSelectedRowsChange) {
      onSelectedRowsChange({ selectedRows: newSelected });
    }
  };

  const handleSelectAll = (e) => {
    e.stopPropagation();
    if (selectedMobileRows.length === data.length) {
      setSelectedMobileRows([]);
      if (onSelectedRowsChange) onSelectedRowsChange({ selectedRows: [] });
    } else {
      setSelectedMobileRows([...data]);
      if (onSelectedRowsChange) onSelectedRowsChange({ selectedRows: [...data] });
    }
  };

  // Filter out columns marked hide='sm' (they are truly hidden on mobile)
  const visibleCols = columns.filter(col => col.hide !== 'sm');

  // Find the index column (#) if it exists, to run its cell renderer for correct page numbering
  const indexCol = columns.find(col => col.name === '#');
  const customSelectionCol = columns.find(col => col.name === 'Select' || typeof col.name !== 'string');
  const allSelected = data.length > 0 && selectedMobileRows.length === data.length;

  return (
    <div className="sdt-mobile-cards">
      {selectableRows && data.length > 0 && (
        <div className="sdt-mobile-select-all mb-2 p-2 bg-light rounded border d-flex align-items-center justify-content-between">
          <label className="d-flex align-items-center gap-2 mb-0 cursor-pointer fw-semibold text-dark" style={{ fontSize: '0.85rem' }}>
            <input
              type="checkbox"
              className="form-check-input"
              checked={allSelected}
              onChange={handleSelectAll}
            />
            Chagua Wageni Wote ({selectedMobileRows.length}/{data.length})
          </label>
        </div>
      )}

      {data.length === 0 ? (
        <div className="sdt-empty-state">
          <FaDatabase className="sdt-empty-icon" />
          <p>Hakuna data iliyopatikana.</p>
        </div>
      ) : (
        data.map((row, rowIndex) => {
          // Calculate the sequence number using the original column's cell renderer if available
          const serialNumber = indexCol && indexCol.cell
            ? indexCol.cell(row, rowIndex)
            : rowIndex + 1;

          const isRowSelected = selectedMobileRows.some(r => String(r.id ?? r) === String(row.id ?? row));

          const customCheckbox = customSelectionCol && customSelectionCol.cell
            ? customSelectionCol.cell(row, rowIndex)
            : null;

          return (
            <div
              key={row.id ?? rowIndex}
              className={`sdt-mobile-card ${isRowSelected ? 'border-primary bg-light' : ''}`}
              onClick={(e) => {
                if (selectableRows) {
                  handleToggleRow(row, e);
                } else if (onRowClicked) {
                  onRowClicked(row);
                }
              }}
              style={{ cursor: selectableRows ? 'pointer' : 'default' }}
            >
              {/* Header section with Serial Number and selection checkbox */}
              <div className="sdt-mobile-card-header d-flex justify-content-between align-items-center">
                <span className="sdt-mobile-card-num">#{serialNumber}</span>
                {customCheckbox ? (
                  <div className="sdt-mobile-card-select" onClick={(e) => e.stopPropagation()}>
                    {customCheckbox}
                  </div>
                ) : selectableRows ? (
                  <div className="sdt-mobile-card-select" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      style={{ transform: 'scale(1.2)', cursor: 'pointer' }}
                      checked={isRowSelected}
                      onChange={(e) => handleToggleRow(row, e)}
                    />
                  </div>
                ) : null}
              </div>

              {visibleCols.map((col, colIndex) => {
                // Skip index column (#) and custom selection column in body since they are shown in the header
                if (col.name === '#' || col === customSelectionCol) return null;

                const value = col.cell
                  ? col.cell(row, rowIndex)
                  : col.selector
                    ? col.selector(row)
                    : null;

                // "Actions" column spans full width at the bottom
                const isActions = col.name === 'Actions' || col.name === 'Vitendo';

                return (
                  <div
                    key={colIndex}
                    className={`sdt-mobile-field${isActions ? ' sdt-mobile-actions' : ''}`}
                  >
                    {!isActions && (
                      <span className="sdt-mobile-label">{typeof col.name === 'string' ? col.name : ''}</span>
                    )}
                    <span className="sdt-mobile-value">{value}</span>
                  </div>
                );
              })}
            </div>
          );
        })
      )}
    </div>
  );
};


const StyledDataTable = ({
  columns,
  data,
  emptyMessage = 'Hakuna data iliyopatikana.',
  pagination = true,
  paginationPerPage = 25,
  paginationRowsPerPageOptions = [25, 50, 100, 200],
  onChangeRowsPerPage,
  paginationServer,
  paginationTotalRows,
  paginationDefaultPage,
  onChangePage,
  progressPending,
  selectableRows,
  onSelectedRowsChange,
  clearSelectedRows,
  pageInfo,
  onRowClicked,
  maxHeight,
  style,
  fixedHeader,
  fixedHeaderScrollHeight,
  ...rest
}) => {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  /* ── Empty-state component ── */
  const EmptyState = (
    <div className="sdt-empty-state">
      <FaDatabase className="sdt-empty-icon" />
      <p>{emptyMessage}</p>
    </div>
  );

  const formattedMaxHeight = maxHeight
    ? (typeof maxHeight === 'number' ? `${maxHeight}px` : maxHeight)
    : undefined;

  const containerStyle = {
    ...(formattedMaxHeight ? { maxHeight: formattedMaxHeight, overflowY: 'auto', overflowX: 'hidden' } : {}),
    ...style
  };

  return (
    <div className="sdt-scroll" style={containerStyle}>
      <div className="sdt-wrapper">

        {/* ── Mobile: render card list instead of DataTable ── */}
        {isMobile ? (
          <MobileCardList
            columns={columns}
            data={data ?? []}
            selectableRows={selectableRows}
            onSelectedRowsChange={onSelectedRowsChange}
            clearSelectedRows={clearSelectedRows}
            onRowClicked={onRowClicked}
          />
        ) : (
          <DataTable
            columns={columns}
            data={data}
            noDataComponent={EmptyState}
            pagination={pagination}
            paginationPerPage={paginationPerPage}
            paginationRowsPerPageOptions={paginationRowsPerPageOptions}
            onChangeRowsPerPage={onChangeRowsPerPage}
            paginationServer={paginationServer}
            paginationTotalRows={paginationTotalRows}
            paginationDefaultPage={paginationDefaultPage}
            onChangePage={onChangePage}
            progressPending={progressPending}
            selectableRows={selectableRows}
            onSelectedRowsChange={onSelectedRowsChange}
            clearSelectedRows={clearSelectedRows}
            onRowClicked={onRowClicked}
            highlightOnHover
            striped
            dense
            responsive={true}
            {...rest}
          />
        )}
      </div>

      {/* Mobile pagination – simple prev/next */}
      {isMobile && pagination && (
        <div className="sdt-mobile-pagination">
          <button
            className="sdt-page-btn"
            disabled={!paginationDefaultPage || paginationDefaultPage <= 1}
            onClick={() => onChangePage && onChangePage(paginationDefaultPage - 1)}
          >
            ‹ Nyuma
          </button>
          <span className="sdt-page-num">
            Uk. {paginationDefaultPage ?? 1}
            {paginationTotalRows ? ` / ${Math.ceil(paginationTotalRows / paginationPerPage)}` : ''}
          </span>
          <button
            className="sdt-page-btn"
            disabled={
              paginationTotalRows &&
              paginationDefaultPage >= Math.ceil(paginationTotalRows / paginationPerPage)
            }
            onClick={() => onChangePage && onChangePage((paginationDefaultPage ?? 1) + 1)}
          >
            Mbele ›
          </button>
        </div>
      )}

      {pageInfo && (
        <div className="sdt-page-info text-center">{pageInfo}</div>
      )}
    </div>
  );
};

export default StyledDataTable;
